import React, { useEffect, useState } from 'react';
import { api, setToken } from '../api/client';

const TIMEZONES = [
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Anchorage', 'Pacific/Honolulu', 'Europe/London', 'Europe/Paris',
  'Asia/Tokyo', 'Asia/Shanghai', 'Australia/Sydney'
];

const CURRENCIES = ['USD', 'EUR', 'GBP', 'JPY', 'CAD', 'AUD'];

export default function Profile() {
  const [user, setUser] = useState(null);
  const [profileForm, setProfileForm] = useState(null);
  const [emailForm, setEmailForm] = useState({ new_email: '', current_password: '' });
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });

  const [profileMsg, setProfileMsg] = useState('');
  const [emailMsg, setEmailMsg] = useState('');
  const [passwordMsg, setPasswordMsg] = useState('');

  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  const [timeoutHours, setTimeoutHours] = useState(0);
  const [timeoutMins, setTimeoutMins] = useState(0);

  const load = () => api.getMe().then(u => {
    setUser(u);
    setProfileForm({
      display_name: u.display_name || '',
      timezone: u.timezone,
      default_currency: u.default_currency,
      notify_price_alerts: u.notify_price_alerts,
      notify_daily_summary: u.notify_daily_summary,
      session_timeout_minutes: u.session_timeout_minutes,
    });
    setTimeoutHours(Math.floor(u.session_timeout_minutes / 60));
    setTimeoutMins(u.session_timeout_minutes % 60);
  });

  useEffect(() => { load(); }, []);

  // Reduces any hours/mins combination to its lowest representation (e.g. 2h 65m -> 3h 5m)
  // and stores the resulting total in profileForm, which is what's sent to the backend.
  const updateTimeout = (hours, mins) => {
    const totalMinutes = Math.max(0, hours) * 60 + Math.max(0, mins);
    setTimeoutHours(Math.floor(totalMinutes / 60));
    setTimeoutMins(totalMinutes % 60);
    setProfileForm(prev => ({ ...prev, session_timeout_minutes: totalMinutes }));
  };

  const saveProfile = async () => {
    setProfileMsg('');
    if (profileForm.session_timeout_minutes < 15 || profileForm.session_timeout_minutes > 720) {
      setProfileMsg('Session timeout must be between 15 minutes and 12 hours.');
      return;
    }
    try {
      await api.updateProfile(profileForm);
      setProfileMsg('Saved.');
      load();
    } catch {
      setProfileMsg('Could not save changes.');
    }
  };

  const openEmailModal = () => { setEmailForm({ new_email: '', current_password: '' }); setEmailMsg(''); setShowEmailModal(true); };
  const closeEmailModal = () => setShowEmailModal(false);

  const saveEmail = async () => {
    setEmailMsg('');
    try {
      await api.updateEmail(emailForm);
      setEmailForm({ new_email: '', current_password: '' });
      load();
      setShowEmailModal(false);
    } catch (e) {
      setEmailMsg('Could not update email. Check your password and try again.');
    }
  };

  const openPasswordModal = () => { setPasswordForm({ current_password: '', new_password: '', confirm_password: '' }); setPasswordMsg(''); setShowPasswordModal(true); };
  const closePasswordModal = () => setShowPasswordModal(false);

  const savePassword = async () => {
    setPasswordMsg('');
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setPasswordMsg('New passwords do not match.');
      return;
    }
    if (passwordForm.new_password.length < 8) {
      setPasswordMsg('New password must be at least 8 characters.');
      return;
    }
    try {
      // Changing the password signs out other sessions; keep this one with the fresh token
      const res = await api.updatePassword({
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      if (res && res.access_token) setToken(res.access_token);
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
      setShowPasswordModal(false);
    } catch {
      setPasswordMsg('Could not update password. Check your current password.');
    }
  };

  if (!user || !profileForm) return <div className="muted" style={{ padding: 40 }}>Loading...</div>;

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Profile</h1>
      </div>

      <div style={{ display: 'grid', gap: 20, maxWidth: 560 }}>

        {/* Account info */}
        <div className="card">
          <div className="modal-title">Account</div>
          <div className="form-group">
            <label>Username</label>
            <input value={user.username} disabled style={{ opacity: 0.6 }} />
          </div>
          <div className="form-group">
            <label>Display Name</label>
            <input
              value={profileForm.display_name}
              onChange={e => setProfileForm({ ...profileForm, display_name: e.target.value })}
              placeholder="How you'd like to be addressed"
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label>Timezone</label>
              <select value={profileForm.timezone} onChange={e => setProfileForm({ ...profileForm, timezone: e.target.value })}>
                {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Default Currency</label>
              <select value={profileForm.default_currency} onChange={e => setProfileForm({ ...profileForm, default_currency: e.target.value })}>
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div className="form-group" style={{ marginTop: 8 }}>
            <label>Notifications</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', textTransform: 'none', fontSize: 14, color: 'var(--text)' }}>
                <input
                  type="checkbox"
                  style={{ width: 'auto' }}
                  checked={profileForm.notify_price_alerts}
                  onChange={e => setProfileForm({ ...profileForm, notify_price_alerts: e.target.checked })}
                />
                Price alert notifications
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', textTransform: 'none', fontSize: 14, color: 'var(--text)' }}>
                <input
                  type="checkbox"
                  style={{ width: 'auto' }}
                  checked={profileForm.notify_daily_summary}
                  onChange={e => setProfileForm({ ...profileForm, notify_daily_summary: e.target.checked })}
                />
                Daily portfolio summary
              </label>
            </div>
          </div>

          <div className="form-group" style={{ marginTop: 8 }}>
            <label>Inactivity Sign-Out</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <input
                  type="number"
                  min={0}
                  value={timeoutHours}
                  onChange={e => updateTimeout(parseInt(e.target.value, 10) || 0, timeoutMins)}
                />
                <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>hours</div>
              </div>
              <div>
                <input
                  type="number"
                  min={0}
                  value={timeoutMins}
                  onChange={e => updateTimeout(timeoutHours, parseInt(e.target.value, 10) || 0)}
                />
                <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>mins.</div>
              </div>
            </div>
            <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>Between 15 minutes and 12 hours.</div>
          </div>

          <div className="modal-actions" style={{ justifyContent: 'flex-start', marginTop: 16 }}>
            <button className="btn" onClick={saveProfile}>Save Changes</button>
            {profileMsg && <span className={profileMsg === 'Saved.' ? 'gain' : 'loss'} style={{ fontSize: 13, alignSelf: 'center' }}>{profileMsg}</span>}
          </div>
        </div>

        {/* Email */}
        <div className="card">
          <div className="modal-title">Email Address</div>
          <div className="muted" style={{ fontSize: 13, marginBottom: 16 }}>Current: {user.email}</div>
          <button className="btn" onClick={openEmailModal}>Change</button>
        </div>

        {/* Password */}
        <div className="card">
          <div className="modal-title">Password</div>
          <button className="btn" onClick={openPasswordModal}>Change</button>
        </div>

      </div>

      {showEmailModal && (
        <div className="modal-overlay" onClick={closeEmailModal}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-title">Update Email</div>
            <div className="form-group">
              <label>New Email</label>
              <input
                value={emailForm.new_email}
                onChange={e => setEmailForm({ ...emailForm, new_email: e.target.value })}
                placeholder="new@email.com"
                autoFocus
              />
            </div>
            <div className="form-group">
              <label>Current Password</label>
              <input
                type="password"
                value={emailForm.current_password}
                onChange={e => setEmailForm({ ...emailForm, current_password: e.target.value })}
                placeholder="••••••••"
              />
            </div>
            {emailMsg && <div className="loss" style={{ fontSize: 13, marginBottom: 12 }}>{emailMsg}</div>}
            <div className="modal-actions">
              <button className="btn-ghost" onClick={closeEmailModal}>Cancel</button>
              <button className="btn" onClick={saveEmail}>Update Email</button>
            </div>
          </div>
        </div>
      )}

      {showPasswordModal && (
        <div className="modal-overlay" onClick={closePasswordModal}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-title">Change Password</div>
            <div className="form-group">
              <label>Current Password</label>
              <input
                type="password"
                value={passwordForm.current_password}
                onChange={e => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                placeholder="••••••••"
                autoFocus
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label>New Password</label>
                <input
                  type="password"
                  value={passwordForm.new_password}
                  onChange={e => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                  placeholder="••••••••"
                />
              </div>
              <div className="form-group">
                <label>Confirm New Password</label>
                <input
                  type="password"
                  value={passwordForm.confirm_password}
                  onChange={e => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                  placeholder="••••••••"
                />
              </div>
            </div>
            {passwordMsg && <div className="loss" style={{ fontSize: 13, marginBottom: 12 }}>{passwordMsg}</div>}
            <div className="modal-actions">
              <button className="btn-ghost" onClick={closePasswordModal}>Cancel</button>
              <button className="btn" onClick={savePassword}>Update Password</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}